/* =============================================================================
 * M.M.LaFleur — bring the filter panel to the shopper on mobile.
 *
 * Reported as: scroll a few rows into a collection, tap Show Filters, and the
 * panel opens somewhere above the viewport. You have to scroll back up to find
 * the thing you just asked for.
 *
 * WHY IT HAPPENS
 * --------------
 * The toolbar is sticky on mobile, so the button stays with you as you scroll.
 * The panel it opens is NOT: sections/mm_collection.liquid renders it as
 * .mm-plp-aside inside .mm-plp-body, in normal flow directly above the product
 * grid — which is where the toolbar was when the page loaded, and hundreds of
 * pixels above where the toolbar is now being tapped. Opening it changes the
 * document but not the scroll position, so nothing appears to happen.
 *
 * The standard failure of a sticky control that opens non-sticky content: the
 * control travels and the content does not.
 *
 * THREE THINGS THAT MADE EARLIER ATTEMPTS SILENTLY DO NOTHING
 * ----------------------------------------------------------
 * 1. MEASURING A STICKY ELEMENT. This is the one that actually mattered.
 *    getBoundingClientRect() on a stuck element returns its STUCK position —
 *    pinned just under the header — not where it sits in the document. So
 *    `toolbar.top + scrollY - offset` resolved to approximately the CURRENT
 *    scroll position no matter how far down the page you were, the
 *    already-in-view guard passed, and the function returned having done
 *    nothing. It failed most convincingly exactly when it was needed most.
 *
 *    The panel is measured instead. It is in normal flow, so its rect is
 *    honest.
 *
 * 2. CAPTURE PHASE. mm-editorial.js binds the toggle on the capture phase and
 *    calls stopPropagation — this theme's house pattern for anything competing
 *    with Barba's link handling. A bubble-phase listener is never reached.
 *
 * 3. THE OPEN STATE LIVES ON THE BODY. The panel opens by flipping
 *    `data-filters` on [data-mm-plp-body]; that is the source of truth read
 *    below. The toggle now also carries aria-expanded / aria-controls, kept in
 *    step by mm-editorial.js (click handler and initFilterPreference), but it
 *    is a mirror for assistive tech, not something to read state from.
 *
 * WHAT IT DOES
 * ------------
 * Scrolls so the top of the panel sits directly beneath the stuck toolbar. The
 * toolbar stays visible — which matters, because the same button is how the
 * panel gets closed again.
 *
 * MOBILE ONLY. Above 1024px the filters are a persistent left rail already in
 * view; moving the page under someone who clicked a rail control would be an
 * unrequested jump.
 * ========================================================================== */

(function () {
  "use strict";

  /* Emitted from mm_product_card.liquid, which renders many times per page.
     The guard means it binds once however often it loads. */
  if (window.__mmPlpFiltersBound) return;
  window.__mmPlpFiltersBound = true;

  var DESKTOP = 1024;

  /* The two fixed elements stacked at the top of the viewport. The custom
     properties are published and kept measured by mm_announcement.liquid, so
     this follows a dismissed announcement bar and a wrapped message without
     needing to know about either. */
  function stickyOffset() {
    var styles = window.getComputedStyle(document.documentElement);
    var announce = parseInt(styles.getPropertyValue("--mm-announce-h"), 10) || 0;
    var header = parseInt(styles.getPropertyValue("--mm-header-h"), 10) || 0;

    /* The token is a design value and can go stale against a header that
       changes height on scroll; a live measurement wins when available. */
    var headerEl = document.querySelector(".mm-header-root");
    if (headerEl) {
      var measured = Math.round(headerEl.getBoundingClientRect().height);
      if (measured > 0) header = measured;
    }

    return announce + header;
  }

  function isOpen() {
    var body = document.querySelector("[data-mm-plp-body]");
    return !!body && body.getAttribute("data-filters") === "true";
  }

  function bringPanelIntoView() {
    if (window.innerWidth >= DESKTOP) return;

    var panel = document.querySelector(".mm-plp-aside");
    if (!panel) return;

    /* The toolbar's HEIGHT is safe to read while it is stuck — only its
       position is affected. Reserving it keeps the toggle on screen above the
       panel rather than scrolling the panel underneath it. */
    var toolbar = document.querySelector(".mm-plp-toolbar");
    var reserve = toolbar ? Math.round(toolbar.getBoundingClientRect().height) : 0;

    var target = panel.getBoundingClientRect().top + window.scrollY - stickyOffset() - reserve;
    if (target < 0) target = 0;

    /* Within a few pixels of where we want to be already — scrolling would
       only jog the page. Compared as a distance in BOTH directions: the panel
       can be below the viewport as well as above it, and an earlier version
       only tested one way. */
    if (Math.abs(window.scrollY - target) < 8) return;

    var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: target, behavior: reduce ? "auto" : "smooth" });
  }

  document.addEventListener("click", function (event) {
    if (!event.target.closest) return;
    if (!event.target.closest("[data-mm-filter-toggle]")) return;

    /* Deferred a frame so the theme's own handler has flipped data-filters and
       the panel has been laid out — its rect is meaningless until then, and
       whether this click OPENED or CLOSED it is only knowable afterwards.

       This listener does NOT preventDefault or stopPropagation: the toggle's
       real handler still has to run, and swallowing the event here would stop
       the panel opening at all. */
    window.requestAnimationFrame(function () {
      if (!isOpen()) return;
      bringPanelIntoView();
    });
  }, true);
}());
